import * as React from 'react';
import Truncate from 'react-truncate';
import { pathOr } from 'ramda';
import {
  IAreaPart,
  IBargainTermsSchema,
  IDeadlineSchema,
  TAreaUnitType,
  TLandStatus,
  TMaterialType,
} from '@cian/api-models/offer-card/v1/get-offer-data';
import { FOfferType } from '../../utils/offer_type';
import { FieldType } from './config';
import * as styles from './Description.css';
import { getInfoValue, ISpecialtyIcon, ISpecialtyView } from './utils';
import { AreaParts } from '../AreaParts';
import { ButtonCollapsed } from '@cian/components';
import { Shutter } from '../Shutter';

export interface IDescriptionProps {
  specialtyPrepared: ISpecialtyView | null;
  specialtyTitle: string;
  offerType: FOfferType;
  minArea: string | null;
  maxArea: string | null;
  availableData: string | undefined;
  commercialLandType: string | undefined;
  ceilingHeight: string | null;
  columnGrid: string | null;
  description: string | null;
  garageType: string;
  offerConfig: FieldType[];
  landAreaUnitType: TAreaUnitType | null;
  totalArea: string | null;
  livingArea: number | null;
  kitchenArea: number | null;
  roomArea: number | null;
  landStatus: TLandStatus | null;
  landArea: number | null;
  buildMaterialType: TMaterialType | null;
  buildYear: number | null;
  deadline: IDeadlineSchema | null;
  floorNumber: number | null;
  floorsCount: number | null;
  parkingType: string | null;
  possibleToChangeStatus: boolean | null;
  propertyType: string;
  isOccupied: boolean | null;
  classType: string;
  title: string | null;
  descriptionMicroData: object;
  areaParts?: IAreaPart[];
  bargainTerms: IBargainTermsSchema;
  isTourAvailable?: boolean;
  minLeaseTerm: number | null;
}

export interface IDescriptionState {
  expanded: boolean;
  truncated: boolean;
}

const LINES = 3;

export class Description extends React.PureComponent<IDescriptionProps, IDescriptionState> {
  public constructor(props: IDescriptionProps) {
    super(props);

    this.state = {
      expanded: false,
      truncated: false,
    };
  }

  private handleTruncate = (truncated: boolean) => {
    if (this.state.truncated !== truncated) {
      this.setState({
        truncated,
      });
    }
  };

  private toggleLines = (event: React.MouseEvent<HTMLSpanElement>) => {
    event.preventDefault();

    this.setState({
      expanded: !this.state.expanded,
    });
  };

  private getSpecialtyClassName = () => {
    const { specialtyPrepared } = this.props;

    return pathOr([], ['specialtiesIcon'], specialtyPrepared).length > 2
      ? 'specialty-vertical'
      : 'specialty-horizontal';
  };

  public render() {
    const { specialtyPrepared, specialtyTitle, offerConfig, areaParts, bargainTerms, title } = this.props;
    const descriptionProps = areaParts && areaParts.length > 0 ? { id: '/area-parts' } : {};

    const { expanded, truncated } = this.state;
    const cssSpecialtyName = this.getSpecialtyClassName();

    return (
      <div id="description">
        <div className={styles['description']}>
          {title && <div className={styles['title_block']}>{title}</div>}

          <div itemScope className={styles['info-block']} {...descriptionProps}>
            {offerConfig.map((config, index) => {
              const value = getInfoValue(config, this.props);

              if (!value) {
                return null;
              }

              return (
                <div className={styles['info']} key={`info_${index}`}>
                  <div className={styles['info-value']}>{value}</div>
                  <div className={styles['info-title']}>{config.title}</div>
                </div>
              );
            })}
          </div>
        </div>
        <div className={styles['description-container']}>
          <div className={styles['description-parts']}>
            <AreaParts areaParts={areaParts} bargainTerms={bargainTerms} />
            <div>{this.renderDescription()}</div>
          </div>
        </div>
        {specialtyPrepared && specialtyPrepared.specialtiesString && (
          <div className={styles['description-container']}>
            <div className={styles['description-specialty']}>
              <div className={styles['specialties-title']}>{specialtyTitle}</div>
              <ul className={styles['specialties']}>
                {specialtyPrepared.specialtiesIcon &&
                  specialtyPrepared.specialtiesIcon.map((iconSpecialty: ISpecialtyIcon, index: number) => {
                    const IconComponent = iconSpecialty.icon;
                    // @ts-ignore
                    const renderIcon = <IconComponent />;

                    return (
                      <li key={`desc-specialty-${index}`} className={styles[cssSpecialtyName]}>
                        <span className={styles['specialty-icon']}>{renderIcon}</span>
                        <span className={styles['specialty-title']}>{iconSpecialty.title}</span>
                      </li>
                    );
                  })}
              </ul>
              <div className={styles['specialties-text']}>
                <Truncate lines={!expanded && LINES} onTruncate={this.handleTruncate} ellipsis={<span>...</span>}>
                  {`Ещё: ${specialtyPrepared.specialtiesString}`}
                </Truncate>
              </div>
              {(truncated || expanded) && (
                <div>
                  <ButtonCollapsed
                    opened={!truncated && expanded}
                    buttonText="Показать всё"
                    closeButtonText="Свернуть"
                    onClick={this.toggleLines}
                    buttonStyle={styles['button_collapsed']}
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  private renderDescription = () => {
    const { description, descriptionMicroData, isTourAvailable = false } = this.props;

    if (!description) {
      return null;
    }

    const desc = (
      <span {...descriptionMicroData}>
        <p {...{ itemProp: 'description' }} className={styles['description-text']}>
          {description}
        </p>
      </span>
    );

    if (isTourAvailable) {
      return <Shutter defaultMaxHeight={90}>{desc}</Shutter>;
    }

    return desc;
  };
}
